// deviceManagement.js — device provisioning and receipt-block supply (admin only).
//
// WHY THIS SCREEN EXISTS AT ALL: `register_device` is admin-only (migration 0005),
// so a cashier device CAN NEVER REGISTER ITSELF, and until now there was no way for
// an admin to register one either. The collection gate refuses an unprovisioned
// device with `device-not-provisioned` (spec §7) — correctly, because a sale queued
// by a device with no identity parks server-side under a NULL `device_id` and
// appears in NO device's conflict queue, which is invariant 6 violated by omission.
//
// Without this screen no cashier device can collect at all. It is the last hard
// blocker before going live, not a convenience.
//
// It does four things, in the order an admin needs them:
//   1. REGISTER a device and show the id it was given.
//   2. LIST the registered devices with the receipt blocks each one owns.
//   3. ALLOCATE a receipt block — a device with an identity but no numbers still
//      cannot collect (gate step 3).
//   4. HAND the id to the cashier machine, which adopts it with `adoptDeviceId()`.
//
// EVERY READ PAGINATES (spec §8, audit C6) and every write reports the server's own
// refusal through `messageFor()`. Nothing here writes to the ledger.

import { supabase } from './supabaseClient.js';
import { showStatus } from './utils.js';

import { db } from './db/schema.js';
import { fetchAllPages, readBlockState, applyBlockArrival } from './db/index.js';
import { getEngine, adoptDeviceId } from './ui/engine-boot.js';
import { messageFor, messageForError } from './ui/engine-messages.js';
import { formatReceiptNo } from './money/index.js';

/**
 * Escape text destined for an `innerHTML` template (audit M7). A device name is
 * admin-typed, but it renders in the admin's own privileged session and the rule
 * does not get an exception for "probably safe" input.
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Default block size, matching `allocate_receipt_block`'s own default (spec §3.2). */
const DEFAULT_BLOCK_SIZE = 1000;

export const DeviceProvisioning = {
    elements: {},
    /** @type {object[]} */
    devices: [],
    /** @type {Map<string, object[]>} device id → its receipt blocks */
    blocksByDevice: new Map(),

    init() {
        this.elements = {
            modal: document.getElementById('device-management-modal'),
            openBtn: document.getElementById('manage-devices-btn'),
            closeBtn: document.getElementById('close-devices-modal'),
            registerForm: document.getElementById('device-register-form'),
            nameInput: document.getElementById('device-name-input'),
            tableBody: document.getElementById('devices-table-body'),
            thisDeviceId: document.getElementById('this-device-id'),
            thisDeviceBlock: document.getElementById('this-device-block'),
            copyBtn: document.getElementById('copy-this-device-id'),
            adoptForm: document.getElementById('adopt-device-form'),
            adoptInput: document.getElementById('adopt-device-input'),
            notice: document.getElementById('device-provisioning-notice')
        };

        this.elements.openBtn?.addEventListener('click', () => this.openModal());
        this.elements.closeBtn?.addEventListener('click', () => this.closeModal());
        this.elements.modal?.addEventListener('click', (e) => {
            if (e.target === this.elements.modal) this.closeModal();
        });
        this.elements.registerForm?.addEventListener('submit', (e) => this.handleRegister(e));
        this.elements.adoptForm?.addEventListener('submit', (e) => this.handleAdopt(e));
        this.elements.copyBtn?.addEventListener('click', () => this.copyThisDeviceId());

        // Delegated row actions — no id is ever interpolated into an attribute.
        this.elements.tableBody?.addEventListener('click', (e) => {
            const button = e.target instanceof Element ? e.target.closest('button[data-action]') : null;
            if (!button) return;
            const deviceId = button.getAttribute('data-device-id');
            const action = button.getAttribute('data-action');
            if (action === 'allocate') void this.allocateBlock(deviceId, false);
            else if (action === 'meta-loss') void this.allocateBlock(deviceId, true);
            else if (action === 'copy') void this.copyText(deviceId, 'Identifiant de l\'appareil copié.');
        });

        console.log('DeviceProvisioning module initialized');
    },

    async openModal() {
        this.elements.modal?.classList.remove('hidden');
        await this.refresh();
    },

    closeModal() {
        this.elements.modal?.classList.add('hidden');
    },

    // ---- reading ----------------------------------------------------------

    async refresh() {
        await Promise.all([this.loadDevices(), this.renderThisDevice()]);
    },

    /**
     * Load every registered device and every receipt block, both paginated.
     *
     * `authenticated` holds SELECT on `devices` and `receipt_blocks` (migration
     * 0007), so this works for the admin without any definer function.
     */
    async loadDevices() {
        const body = this.elements.tableBody;
        if (!body) return;
        body.innerHTML = '<tr><td colspan="5" class="text-center text-muted">Chargement des appareils…</td></tr>';

        try {
            const [devices, blocks] = await Promise.all([
                fetchAllPages((from, to) => supabase
                    .from('devices')
                    .select('id, name, is_active, created_at, registered_by')
                    .order('created_at', { ascending: true })
                    .order('id', { ascending: true })
                    .range(from, to)),
                fetchAllPages((from, to) => supabase
                    .from('receipt_blocks')
                    .select('id, device_id, block_start, block_end, reason, allocated_at, retired_at')
                    .order('block_start', { ascending: true })
                    .order('id', { ascending: true })
                    .range(from, to))
            ]);

            this.devices = devices;
            this.blocksByDevice = new Map();
            for (const block of blocks) {
                const list = this.blocksByDevice.get(block.device_id) || [];
                list.push(block);
                this.blocksByDevice.set(block.device_id, list);
            }
            this.renderDevices();
        } catch (error) {
            console.error('Chargement des appareils impossible:', error);
            body.innerHTML =
                '<tr><td colspan="5" class="text-center" style="color: var(--danger-600);">Erreur de chargement des appareils</td></tr>';
            showStatus(`Chargement des appareils impossible : ${error.message}`, true);
        }
    },

    renderDevices() {
        const body = this.elements.tableBody;
        if (!body) return;
        body.innerHTML = '';

        if (this.devices.length === 0) {
            body.innerHTML =
                '<tr><td colspan="5" class="text-center text-muted">Aucun appareil enregistré. '
                + 'Tant qu\'un poste n\'est pas enregistré, il ne peut pas encaisser.</td></tr>';
            return;
        }

        const engine = getEngine();
        const thisDeviceId = engine ? engine.deviceId : null;

        for (const device of this.devices) {
            const blocks = this.blocksByDevice.get(device.id) || [];
            const live = blocks.filter(b => !b.retired_at);
            const blockText = blocks.length === 0
                ? 'Aucun carnet — ce poste ne peut pas encaisser'
                : live.map(b => `${formatReceiptNo(b.block_start)}–${formatReceiptNo(b.block_end)}`).join(', ')
                    || 'Tous les carnets sont retirés';
            const retiredText = blocks.length > live.length
                ? ` (+${blocks.length - live.length} retiré(s))`
                : '';

            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td class="font-medium">
                    ${escapeHtml(device.name)}
                    ${device.id === thisDeviceId ? '<span class="badge badge-success">cet appareil</span>' : ''}
                </td>
                <td class="text-xs text-muted"><code>${escapeHtml(device.id)}</code></td>
                <td>${device.is_active
                    ? '<span class="badge badge-success">Actif</span>'
                    : '<span class="badge badge-danger">Désactivé</span>'}</td>
                <td class="text-sm">${escapeHtml(blockText)}${escapeHtml(retiredText)}</td>
                <td class="text-center"></td>`;

            const actions = tr.lastElementChild;
            actions.appendChild(this.actionButton('copy', device.id, 'Copier l\'ID', 'btn btn-secondary btn-sm'));
            actions.appendChild(this.actionButton('allocate', device.id, 'Nouveau carnet', 'btn btn-primary btn-sm'));
            actions.appendChild(this.actionButton(
                'meta-loss', device.id, 'Après perte de données', 'btn btn-warning btn-sm'));
            body.appendChild(tr);
        }
    },

    /**
     * @param {string} action
     * @param {string} deviceId
     * @param {string} label
     * @param {string} className
     * @returns {HTMLButtonElement}
     */
    actionButton(action, deviceId, label, className) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.textContent = label;
        button.setAttribute('data-action', action);
        button.setAttribute('data-device-id', deviceId);
        return button;
    },

    /**
     * This machine's own identity and receipt supply.
     *
     * Reads the LOCAL meta, not the server: the question "can THIS browser collect
     * right now?" is answered by what this browser can prove it owns (invariant 3).
     */
    async renderThisDevice() {
        const engine = getEngine();
        const deviceId = engine ? engine.deviceId : null;

        if (this.elements.thisDeviceId) {
            this.elements.thisDeviceId.value = deviceId || '';
            this.elements.thisDeviceId.placeholder = 'Cet appareil n\'est pas encore enregistré';
        }

        if (!this.elements.thisDeviceBlock) return;
        try {
            const block = await readBlockState(db);
            if (!deviceId) {
                this.elements.thisDeviceBlock.textContent = messageFor('device-not-provisioned');
            } else if (!block.hasBlock) {
                this.elements.thisDeviceBlock.textContent =
                    'Aucun carnet de numéros sur cet appareil — ' + messageFor('no-receipt-numbers');
            } else {
                this.elements.thisDeviceBlock.textContent =
                    `Carnet ${formatReceiptNo(block.start)}–${formatReceiptNo(block.end)}, `
                    + `prochain numéro ${formatReceiptNo(block.next)}, `
                    + `${block.remaining} restant(s)`
                    + (block.exhausted ? ' — épuisé.' : block.low ? ' — bientôt épuisé.' : '.');
            }
        } catch (error) {
            this.elements.thisDeviceBlock.textContent =
                `État du carnet illisible : ${error.message}`;
        }
    },

    // ---- writes -----------------------------------------------------------

    /**
     * The engine handle, or a French refusal. Registration and allocation are
     * authenticated RPCs; neither has an offline path.
     * @returns {{engine:object}|{error:string}}
     */
    requireEngine() {
        const engine = getEngine();
        if (!engine || !engine.transport) return { error: messageFor('engine-not-ready') };
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return {
                error: 'L\'enregistrement d\'un appareil exige une connexion au serveur. '
                    + 'Rétablissez le réseau, puis recommencez.'
            };
        }
        return { engine };
    },

    /**
     * @param {Event} e
     */
    async handleRegister(e) {
        e.preventDefault();
        const name = (this.elements.nameInput?.value || '').trim();
        if (!name) {
            showStatus('Donnez un nom reconnaissable à l\'appareil, par exemple « Caisse A — portable ».', true);
            return;
        }
        if (name.length > 120) {
            showStatus('Le nom de l\'appareil dépasse 120 caractères.', true);
            return;
        }

        const ready = this.requireEngine();
        if ('error' in ready) {
            showStatus(ready.error, true);
            return;
        }

        const submit = this.elements.registerForm?.querySelector('button[type="submit"]');
        if (submit) submit.disabled = true;
        try {
            showStatus('Enregistrement de l\'appareil…', false);
            const result = await ready.engine.transport.registerDevice(name);
            const id = result && (result.device_id || result.id);
            if (!id) throw new Error('le serveur n\'a renvoyé aucun identifiant');

            if (this.elements.nameInput) this.elements.nameInput.value = '';
            await this.loadDevices();
            this.setNotice(
                `Appareil « ${name} » enregistré. Identifiant : ${id}. `
                + 'Attribuez-lui un carnet de numéros, puis saisissez cet identifiant sur le poste concerné '
                + '(« Adopter un identifiant » ci-dessous, sur la machine du caissier).');
            showStatus('Appareil enregistré.', false);
        } catch (error) {
            console.error('Enregistrement de l\'appareil refusé:', error);
            // A non-admin caller gets `42501` from the RPC — say who may do this.
            showStatus(error && error.code === '42501'
                ? messageFor('caller-not-admin')
                : messageForError(error), true);
        } finally {
            if (submit) submit.disabled = false;
        }
    },

    /**
     * Allocate a receipt block.
     *
     * `metaLoss` retires this device's PRIOR blocks server-side and hands back a
     * brand-new range. That is the correct answer after a device lost its local
     * state, and the only one: a partially consumed block is NEVER resumed from
     * fresh state (invariant 3) because that re-issues numbers already printed on
     * paper in patients' hands. Retirement does not invalidate anything — a
     * late-syncing event on a retired block is still accepted.
     *
     * @param {string|null} deviceId
     * @param {boolean} metaLoss
     */
    async allocateBlock(deviceId, metaLoss) {
        if (!deviceId) return;
        const device = this.devices.find(d => d.id === deviceId);
        const label = device ? device.name : deviceId;
        const hasBlocks = (this.blocksByDevice.get(deviceId) || []).length > 0;
        const reason = metaLoss ? 'meta_loss' : hasBlocks ? 'low_watermark' : 'initial';

        const confirmText = metaLoss
            ? `Attribuer un carnet NEUF à « ${label} » et RETIRER ses carnets actuels ?\n\n`
                + 'À faire uniquement lorsque le poste a perdu ses données locales. '
                + 'Les numéros déjà émis restent valables ; le poste repart d\'une plage entièrement neuve.'
            : `Attribuer un nouveau carnet de ${DEFAULT_BLOCK_SIZE} numéros à « ${label} » ?`;
        if (!confirm(confirmText)) return;

        const ready = this.requireEngine();
        if ('error' in ready) {
            showStatus(ready.error, true);
            return;
        }

        try {
            showStatus('Attribution du carnet…', false);
            const block = await ready.engine.transport.allocateReceiptBlock({
                deviceId, reason, size: DEFAULT_BLOCK_SIZE
            });
            const range = `${formatReceiptNo(block.block_start)}–${formatReceiptNo(block.block_end)}`;

            // A block allocated for THIS browser is not adopted automatically. Adopting
            // it abandons the unused tail of the current range (spec §10: an unused
            // tail of a live block is not a gap) — correct, but it must be a decision,
            // never a side effect of clicking a button in an admin list.
            if (ready.engine.deviceId === deviceId) {
                const adopt = confirm(
                    `Carnet ${range} attribué à cet appareil.\n\n`
                    + 'L\'utiliser dès maintenant sur cet appareil ? '
                    + 'La fin du carnet en cours sera abandonnée (elle ne sera jamais réémise).');
                if (adopt) {
                    await applyBlockArrival(db, block);
                    await ready.engine.publishStatus();
                }
            }

            await this.refresh();
            this.setNotice(`Carnet ${range} attribué à « ${label} » (motif : ${reason}).`);
            showStatus(`Carnet ${range} attribué.`, false);
        } catch (error) {
            console.error('Attribution du carnet refusée:', error);
            showStatus(messageForError(error), true);
        }
    },

    /**
     * Adopt an identity an admin issued elsewhere, on THIS machine.
     *
     * `adoptDeviceId` refuses to overwrite an existing identity: re-pointing a
     * device that has already consumed receipt numbers would make its numbers fall
     * outside the new device's blocks (`receipt-no-outside-device-blocks`) and park
     * every subsequent sale.
     * @param {Event} e
     */
    async handleAdopt(e) {
        e.preventDefault();
        const value = (this.elements.adoptInput?.value || '').trim();
        if (!value) return;

        try {
            const result = await adoptDeviceId(value);
            if (!result.adopted) {
                showStatus(result.reason === 'device-already-provisioned'
                    ? 'Cet appareil possède déjà une identité différente. Ne la remplacez pas : '
                        + 'ses numéros de reçu appartiennent à ses propres carnets. '
                        + 'Faites enregistrer un nouvel appareil si nécessaire.'
                    : messageFor(result.reason), true);
                return;
            }
            if (this.elements.adoptInput) this.elements.adoptInput.value = '';
            showStatus('Identité adoptée. Attribuez un carnet de numéros à cet appareil s\'il n\'en a pas encore.', false);
            await this.refresh();
        } catch (error) {
            console.error('Adoption de l\'identifiant refusée:', error);
            showStatus(messageForError(error), true);
        }
    },

    async copyThisDeviceId() {
        const value = this.elements.thisDeviceId?.value || '';
        if (!value) {
            showStatus(messageFor('device-not-provisioned'), true);
            return;
        }
        await this.copyText(value, 'Identifiant de cet appareil copié.');
    },

    /**
     * @param {string|null} text
     * @param {string} message
     */
    async copyText(text, message) {
        if (!text) return;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                showStatus(message, false);
                return;
            }
        } catch {
            // Clipboard permission denied or unavailable — fall through and SHOW the
            // value instead of pretending the copy worked.
        }
        this.setNotice(`Identifiant à recopier : ${text}`);
        showStatus('Copie automatique indisponible — l\'identifiant est affiché ci-dessous.', true);
    },

    /** @param {string} text */
    setNotice(text) {
        const el = this.elements.notice;
        if (!el) return;
        el.textContent = text;
        el.classList.toggle('hidden', !text);
    }
};

window.DeviceProvisioning = DeviceProvisioning;
